import React, { useEffect, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";

import Button from "common/components/CustomButtons/Button";
import { formatCurrency, formatPercentage } from "common/utils/format.util";

const pct = x => (typeof x === "number" ? formatPercentage(x) : "n/a");

// Shown when the server blocks a save: discount pushes cap-out under 35%, or the price of an already quoted estimate would drop.
export default function DiscountGateDialog({ gate, onCancel, onSubmit }) {
  const [reason, setReason] = useState("");
  const [approverEmail, setApproverEmail] = useState("");
  const [approverPassword, setApproverPassword] = useState("");

  useEffect(() => {
    if (gate) setApproverPassword("");
  }, [gate]);

  if (!gate) return null;

  const kinds = gate.kinds || [];
  const floorHit = kinds.includes("margin_floor");
  const cutHit = kinds.includes("post_quote_cut");
  const selfApproval = !!gate.selfApproval;
  const canSubmit = reason.trim().length >= 5 && (selfApproval || (approverEmail && approverPassword));

  const submit = () => onSubmit({
    reason: reason.trim(),
    approverEmail: selfApproval && !approverEmail ? undefined : approverEmail,
    approverPassword: selfApproval && !approverEmail ? undefined : approverPassword
  });

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Manager override required</DialogTitle>
      <DialogContent>
        {floorHit && (
          <Typography paragraph>
            <strong>Discount below the cap-out floor.</strong><br/>
            Cap-out would be <strong>{pct(gate.capoutAfter)}</strong> (minimum {pct(gate.floor)}, target {pct(gate.target)}).<br/>
            Without the discount/price reduction it is {pct(gate.capoutWithoutReductions)}: the reduction
            ({formatCurrency(gate.reductionsTotal)}) costs <strong>{gate.reductionCostPoints} cap-out points</strong>.
          </Typography>
        )}
        {cutHit && (
          <Typography paragraph>
            <strong>Price cut on an estimate that was already quoted.</strong><br/>
            Price before {formatCurrency(gate.priceBefore)}, after {formatCurrency(gate.priceAfter)}
            {gate.priceCut ? ` (-${formatCurrency(gate.priceCut)})` : ""}.
          </Typography>
        )}
        {gate.overrideError && (
          <Typography paragraph color="error"><strong>{gate.overrideError}</strong></Typography>
        )}
        <TextField
          label="Reason for the override (required, saved in the audit log)"
          value={reason}
          onChange={e => setReason(e.target.value)}
          fullWidth multiline margin="dense" autoFocus
        />
        {selfApproval ? (
          <Typography variant="body2">You are a listed approver: your name is recorded as the approver. (Leave the fields below empty.)</Typography>
        ) : (
          <Typography variant="body2">Approver (manager) credentials:</Typography>
        )}
        <TextField
          label={selfApproval ? "Other approver e-mail (optional)" : "Approver e-mail"}
          value={approverEmail}
          onChange={e => setApproverEmail(e.target.value)}
          fullWidth margin="dense" autoComplete="off"
        />
        {(!selfApproval || approverEmail) && (
          <TextField
            label="Approver password"
            type="password"
            value={approverPassword}
            onChange={e => setApproverPassword(e.target.value)}
            fullWidth margin="dense" autoComplete="new-password"
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} color="transparent">Cancel (change the estimate)</Button>
        <Button onClick={submit} color="warning" disabled={!canSubmit}>Approve override &amp; save</Button>
      </DialogActions>
    </Dialog>
  );
}
